// PeekPager — the peek footer's REVIEW-WALK pager (ADR-036 §2): "n of m" +
// prev/next, for pages that use the peek as a backlog walker (filter with the
// list chips, then step item-by-item — review / validate / fill-missing flows).
// It is a footer-slot COMPOSITE, not a ContextPanel feature: peeks that don't
// walk simply put their action buttons in the footer slot instead.
//
// The consumer derives the order LIVE from its visible rows (never captured at
// click-time — a captured order dies on deep link / refresh / Back-restore) and
// passes the pre-t()'d label; the rails own no i18n namespace (CONVENTIONS §i18n).
// `label` absent = the open item is not in the current list (filtered out):
// the counter hides and both arrows read disabled — an honest dead-end, not 1/0.

import type { ReactNode } from 'react';
import { IconButton } from '../ui/IconButton.js';
import { icons } from '../ui/icons.js';

export interface PeekPagerProps {
  /** The counter text ("3 of 41", consumer-localized); omit when the open item
   *  is not in the current visible order. */
  label?: ReactNode;
  prevLabel: string;
  nextLabel: string;
  onPrev: () => void;
  onNext: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
}

export function PeekPager({ label, prevLabel, nextLabel, onPrev, onNext, prevDisabled = false, nextDisabled = false }: PeekPagerProps) {
  return (
    // ONE compact group — "n of m" beside its arrows — anchored at the footer's
    // end (architect-ruled): the count reads with the arrows it counts for.
    <div className="ms-auto inline-flex items-center gap-2">
      <span className="text-xs tabular-nums text-[var(--color-text-secondary)]">{label}</span>
      <span className="inline-flex items-center gap-1">
        <IconButton aria-label={prevLabel} disabled={prevDisabled} onClick={onPrev}>
          <icons.caretLeft size={16} aria-hidden className="rtl:-scale-x-100" />
        </IconButton>
        <IconButton aria-label={nextLabel} disabled={nextDisabled} onClick={onNext}>
          <icons.caretRight size={16} aria-hidden className="rtl:-scale-x-100" />
        </IconButton>
      </span>
    </div>
  );
}
